/* Localization - land on the URL without a trailing slash after a language/country switch */
(() => {
    const nativeSubmit = HTMLFormElement.prototype.submit;

    const submitLocalization = async (form) => {
        if (form.dataset.submitting === 'true') return;
        form.dataset.submitting = 'true';
        window.addEventListener('pageshow', () => delete form.dataset.submitting, { once: true });

        const fallback = (reason) => {
            console.warn('Localization: native submit used.', reason);
            nativeSubmit.call(form);
        };

        try {
            const response = await fetch(form.action, {
                method: 'POST',
                body: new FormData(form),
                credentials: 'same-origin',
                redirect: 'follow',
            });

            const target = new URL(response.url);
            const redirected = response.url !== new URL(form.action, window.location.href).href;

            if (response.body) response.body.cancel();

            if (!response.ok) return fallback('status ' + response.status + ' at ' + response.url);
            if (!redirected) return fallback('no redirect, stayed on ' + response.url);
            if (target.origin !== window.location.origin) return fallback('cross-origin ' + target.origin);

            if (target.pathname.length > 1) {
                target.pathname = target.pathname.replace(/\/+$/, '');
            }

            window.location.assign(target.toString());
        } catch (error) {
            fallback(error && error.message ? error.message : 'fetch failed');
        }
    };

    window.homediqSubmitLocalization = submitLocalization;

    HTMLFormElement.prototype.submit = function () {
        if (this.matches('[data-localization-form], [data-mobile-localization-form]')) {
            submitLocalization(this);
            return;
        }
        nativeSubmit.call(this);
    };
})();

/* Header */
class HomediqSearchToggle extends HTMLElement {
    connectedCallback() {
        this.openBtn = this.querySelector('[data-search-open]');
        this.header = this.closest('.homediq-header-wrap');
        this.panel = this.header ? this.header.querySelector('[data-search-panel]') : null;
        this.input = this.panel ? this.panel.querySelector('[data-search-input]') : null;
        this.closeBtn = this.panel ? this.panel.querySelector('[data-search-close]') : null;
        if (!this.openBtn || !this.panel || !this.input) return;
        this.openBtn.addEventListener('click', () => this.toggle());
        if (this.closeBtn) {
            this.closeBtn.addEventListener('click', () => this.close());
        }
        document.addEventListener('keydown', (event) => {
            if (event.key === 'Escape' && !this.panel.hidden) {
                this.close();
            }
        });
    }

    toggle() {
        if (this.panel.hidden) {
            this.open();
        } else {
            this.close();
        }
    }

    open() {
        this.panel.hidden = false;
        this.openBtn.setAttribute('aria-expanded', 'true');
        this.input.focus();
    }

    close() {
        this.panel.hidden = true;
        this.openBtn.setAttribute('aria-expanded', 'false');
    }
}

customElements.define('homediq-search-toggle', HomediqSearchToggle);

class HomediqLocalizationToggle extends HTMLElement {
    connectedCallback() {
        this.button = this.querySelector('[data-localization-open]');
        this.panel = this.querySelector('[data-localization-panel]');
        if (!this.button || !this.panel) return;

        this.button.addEventListener('click', (event) => {
            event.stopPropagation();
            this.toggle();
        });

        document.addEventListener('click', (event) => {
            if (!this.contains(event.target)) {
                this.close();
            }
        });

        document.addEventListener('keydown', (event) => {
            if (event.key === 'Escape') {
                this.close();
            }
        });

        const form = this.panel.querySelector('[data-localization-form]');
        const countryInput = this.panel.querySelector('[data-country-input]');
        const languageInput = this.panel.querySelector('[data-language-input]');

        this.panel.querySelectorAll('[data-country-option]').forEach((btn) => {
            btn.addEventListener('click', () => {
                if (countryInput) countryInput.value = btn.getAttribute('data-country-option');
                const countryLanguage = btn.getAttribute('data-country-language');
                if (languageInput && countryLanguage) languageInput.value = countryLanguage;
                if (form) form.submit();
            });
        });

        this.panel.querySelectorAll('[data-language-option]').forEach((btn) => {
            btn.addEventListener('click', () => {
                if (languageInput) languageInput.value = btn.getAttribute('data-language-option');
                if (form) form.submit();
            });
        });
    }

    toggle() {
        if (this.panel.hidden) {
            this.open();
        } else {
            this.close();
        }
    }

    open() {
        this.panel.hidden = false;
        this.button.setAttribute('aria-expanded', 'true');
    }

    close() {
        this.panel.hidden = true;
        this.button.setAttribute('aria-expanded', 'false');
    }
}

customElements.define('homediq-localization-toggle', HomediqLocalizationToggle);

/* Generic dropdown (desktop Tests menu, and any future simple button+panel dropdown) */
class HomediqDropdownToggle extends HTMLElement {
    connectedCallback() {
        this.button = this.querySelector('[data-dropdown-open]');
        this.panel = this.querySelector('[data-dropdown-panel]');
        if (!this.button || !this.panel) return;

        this.button.addEventListener('click', (event) => {
            event.stopPropagation();
            this.toggle();
        });

        document.addEventListener('click', (event) => {
            if (!this.contains(event.target)) {
                this.close();
            }
        });

        document.addEventListener('keydown', (event) => {
            if (event.key === 'Escape') {
                this.close();
            }
        });
    }

    toggle() {
        if (this.panel.hidden) {
            this.open();
        } else {
            this.close();
        }
    }

    open() {
        this.panel.hidden = false;
        this.button.setAttribute('aria-expanded', 'true');
    }

    close() {
        this.panel.hidden = true;
        this.button.setAttribute('aria-expanded', 'false');
    }
}

customElements.define('homediq-dropdown-toggle', HomediqDropdownToggle);

/* Mobile menu */
class HomediqMobileMenuToggle extends HTMLElement {
    connectedCallback() {
        this.openBtn = this.querySelector('[data-mobile-menu-open]');
        this.drawer = this.querySelector('[data-mobile-drawer]');
        this.closeBtns = this.querySelectorAll('[data-mobile-drawer-close]');
        if (!this.openBtn || !this.drawer) return;

        this.openBtn.addEventListener('click', () => this.open());
        this.closeBtns.forEach((btn) => {
            btn.addEventListener('click', () => this.close());
        });

        document.addEventListener('keydown', (event) => {
            if (event.key === 'Escape' && !this.drawer.hidden) {
                this.close();
            }
        });

        const form = this.querySelector('[data-mobile-localization-form]');
        const countryInput = this.querySelector('[data-mobile-country-input]');
        const languageInput = this.querySelector('[data-mobile-language-input]');

        this.querySelectorAll('[data-mobile-country-option]').forEach((btn) => {
            btn.addEventListener('click', () => {
                if (countryInput) countryInput.value = btn.getAttribute('data-mobile-country-option');
                const countryLanguage = btn.getAttribute('data-country-language');
                if (languageInput && countryLanguage) languageInput.value = countryLanguage;
                if (form) form.submit();
            });
        });

        this.querySelectorAll('[data-mobile-language-option]').forEach((btn) => {
            btn.addEventListener('click', () => {
                if (languageInput) languageInput.value = btn.getAttribute('data-mobile-language-option');
                if (form) form.submit();
            });
        });

        this.querySelectorAll('[data-mobile-tests-toggle]').forEach((toggle) => {
            const panel = toggle.parentElement ? toggle.parentElement.querySelector('[data-mobile-tests-panel]') : null;
            if (!panel) return;

            toggle.addEventListener('click', () => {
                const isOpen = toggle.getAttribute('aria-expanded') === 'true';
                toggle.setAttribute('aria-expanded', String(!isOpen));
                panel.hidden = isOpen;
            });
        });
    }

    open() {
        this.drawer.hidden = false;
        this.openBtn.setAttribute('aria-expanded', 'true');
        document.body.style.overflow = 'hidden';
    }

    close() {
        this.drawer.hidden = true;
        this.openBtn.setAttribute('aria-expanded', 'false');
        document.body.style.overflow = '';
    }
}

customElements.define('homediq-mobile-menu-toggle', HomediqMobileMenuToggle);

/* Mega menu */
class HomediqMegaMenu extends HTMLElement {
    connectedCallback() {
        this.button = this.querySelector('[data-mega-open]');
        this.panel = this.querySelector('[data-mega-panel]');
        if (!this.button || !this.panel) return;

        this.hoverTimeout = null;

        this.addEventListener('mouseenter', () => {
            clearTimeout(this.hoverTimeout);
            this.classList.add('is-hover-open');
            this.syncExpanded();
        });

        this.addEventListener('mouseleave', () => {
            this.classList.remove('is-dismissed');
            this.hoverTimeout = setTimeout(() => {
                this.classList.remove('is-hover-open');
                this.syncExpanded();
            }, 250);
        });

        this.addEventListener('focusout', (event) => {
            const next = event.relatedTarget;
            if (next && !this.contains(next)) {
                this.close();
                this.classList.remove('is-hover-open');
                this.syncExpanded();
            }
        });

        this.button.addEventListener('click', (event) => {
            event.preventDefault();
            this.toggle();
        });

        document.addEventListener('click', (event) => {
            if (!this.contains(event.target)) {
                this.close();
                this.classList.remove('is-hover-open');
                this.syncExpanded();
            }
        });

        document.addEventListener('keydown', (event) => {
            if (event.key !== 'Escape' || !this.isOpen()) return;

            const focusWasInPanel = this.panel.contains(document.activeElement);
            this.dismiss();
            if (focusWasInPanel) this.button.focus();
        });

        this.categoryButtons = Array.from(this.panel.querySelectorAll('[data-mega-category]'));
        this.productPanels = Array.from(this.panel.querySelectorAll('[data-mega-products-panel]'));

        this.categoryButtons.forEach((btn, index) => {
            btn.addEventListener('mouseenter', () => this.activate(btn.getAttribute('data-mega-category')));
            btn.addEventListener('click', () => this.activate(btn.getAttribute('data-mega-category')));
            btn.addEventListener('keydown', (event) => this.onCategoryKeydown(event, index));
        });

        this.productPanels.forEach((productPanel) => {
            productPanel.addEventListener('keydown', (event) => {
                if (event.key === 'ArrowLeft') {
                    event.preventDefault();
                    this.focusActiveCategory();
                }
            });
        });

        const initial = this.categoryButtons.find((btn) => btn.classList.contains('is-active')) || this.categoryButtons[0];
        if (initial) this.activate(initial.getAttribute('data-mega-category'));
    }

    activate(index) {
        this.categoryButtons.forEach((btn) => {
            const active = btn.getAttribute('data-mega-category') === index;
            btn.classList.toggle('is-active', active);
            btn.setAttribute('tabindex', active ? '0' : '-1');
            btn.setAttribute('aria-current', String(active));
        });
        this.productPanels.forEach((panel) => {
            panel.classList.toggle('is-active', panel.getAttribute('data-mega-products-panel') === index);
        });
    }

    onCategoryKeydown(event, index) {
        const last = this.categoryButtons.length - 1;
        let target;

        switch (event.key) {
            case 'ArrowDown':
                target = index === last ? 0 : index + 1;
                break;
            case 'ArrowUp':
                target = index === 0 ? last : index - 1;
                break;
            case 'Home':
                target = 0;
                break;
            case 'End':
                target = last;
                break;
            case 'ArrowRight':
                event.preventDefault();
                this.focusFirstProductLink();
                return;
            default:
                return;
        }

        event.preventDefault();
        const next = this.categoryButtons[target];
        this.activate(next.getAttribute('data-mega-category'));
        next.focus();
    }

    focusFirstProductLink() {
        const activePanel = this.productPanels.find((panel) => panel.classList.contains('is-active'));
        const link = activePanel && activePanel.querySelector('a[href]');
        if (link) link.focus();
    }

    focusActiveCategory() {
        const active = this.categoryButtons.find((btn) => btn.classList.contains('is-active'));
        if (active) active.focus();
    }

    toggle() {
        if (this.panel.hidden) {
            this.open();
        } else {
            this.close();
        }
    }

    open() {
        this.classList.remove('is-dismissed');
        this.panel.hidden = false;
        this.syncExpanded();
    }

    close() {
        this.panel.hidden = true;
        this.syncExpanded();
    }

    dismiss() {
        clearTimeout(this.hoverTimeout);
        this.close();
        this.classList.remove('is-hover-open');
        if (this.matches(':hover')) this.classList.add('is-dismissed');
        this.syncExpanded();
    }

    isOpen() {
        return (
            !this.panel.hidden ||
            this.classList.contains('is-hover-open') ||
            (this.matches(':hover') && !this.classList.contains('is-dismissed'))
        );
    }

    syncExpanded() {
        this.button.setAttribute('aria-expanded', String(this.isOpen()));
    }
}

customElements.define('homediq-mega-menu', HomediqMegaMenu);

/* Blog list */
class HomediqBlogFilter extends HTMLElement {
    connectedCallback() {
        this.searchInput = this.querySelector('[data-blog-search-input]');
        this.pillsWrap = this.querySelector('[data-blog-filter-pills]');
        this.resultsCount = this.querySelector('[data-blog-results-count]');
        this.cards = document.querySelectorAll('[data-blog-card]');
        this.emptyState = document.querySelector('[data-blog-empty-state]');
        this.showMoreWrap = document.querySelector('[data-blog-show-more-wrap]');
        this.showMoreButton = document.querySelector('[data-blog-show-more]');
        this.pageSize = parseInt(this.getAttribute('data-show-more-amount'), 10) || 0;
        this.initialCount = parseInt(this.getAttribute('data-show-more-initial'), 10) || this.pageSize;
        this.totalPages = parseInt(this.getAttribute('data-total-pages'), 10) || 1;
        this.sectionId = this.getAttribute('data-section-id');
        this.limit = this.initialCount;
        this.loading = false;
        this.activeTag = '';

        if (!this.searchInput || !this.pillsWrap) return;

        this.searchInput.addEventListener('input', () => {
            this.limit = this.initialCount;
            this.applyFilters();
        });

        this.pillsWrap.querySelectorAll('[data-filter-tag]').forEach((pill) => {
            pill.addEventListener('click', () => {
                this.pillsWrap.querySelectorAll('[data-filter-tag]').forEach((btn) => {
                    btn.classList.remove('is-active');
                });
                pill.classList.add('is-active');
                this.activeTag = pill.getAttribute('data-filter-tag');
                this.limit = this.initialCount;
                this.applyFilters();
            });
        });

        if (this.showMoreButton) {
            this.showMoreButton.addEventListener('click', () => {
                this.limit += this.pageSize;
                this.applyFilters(true);
            });
        }

        this.loadRemainingPages();
    }

    async loadRemainingPages() {
        const grid = document.querySelector('.homediq-blog-list__grid');
        if (this.totalPages <= 1 || !this.sectionId || !grid) return;

        this.loading = true;
        this.applyFilters();

        const pages = [];
        for (let page = 2; page <= this.totalPages; page += 1) pages.push(page);

        for (let i = 0; i < pages.length; i += 4) {
            const results = await Promise.allSettled(pages.slice(i, i + 4).map((page) => this.fetchPageCards(page)));

            results.forEach((result) => {
                if (result.status === 'fulfilled') result.value.forEach((card) => grid.appendChild(card));
            });

            this.refresh();
        }

        this.loading = false;
        this.refresh();
    }

    async fetchPageCards(page) {
        const url = window.location.pathname + '?section_id=' + encodeURIComponent(this.sectionId) + '&page=' + page;
        let lastError;

        for (let attempt = 0; attempt < 3; attempt += 1) {
            try {
                const response = await fetch(url, { credentials: 'same-origin' });
                if (!response.ok) throw new Error('status ' + response.status);

                const doc = new DOMParser().parseFromString(await response.text(), 'text/html');

                return Array.from(doc.querySelectorAll('[data-blog-card]')).map((card) => {
                    const imported = document.importNode(card, true);
                    imported.hidden = true;
                    return imported;
                });
            } catch (error) {
                lastError = error;
                if (attempt < 2) await new Promise((resolve) => setTimeout(resolve, 800 * (attempt + 1)));
            }
        }

        console.warn('Blog list: could not load page ' + page, lastError);
        throw lastError;
    }

    refresh() {
        this.cards = document.querySelectorAll('[data-blog-card]');
        this.updateCounts();
        this.applyFilters();
    }

    updateCounts() {
        const counts = {};

        this.cards.forEach((card) => {
            (card.getAttribute('data-tags') || '').split(',').forEach((tag) => {
                if (tag) counts[tag] = (counts[tag] || 0) + 1;
            });
        });

        this.pillsWrap.querySelectorAll('[data-filter-tag]').forEach((pill) => {
            const tag = pill.getAttribute('data-filter-tag');
            const count = tag === '' ? this.cards.length : counts[tag] || 0;
            const value = pill.querySelector('[data-count-value]');

            pill.setAttribute('data-count', String(count));
            if (value) value.textContent = String(count);
        });
    }

    applyFilters(focusFirstRevealed = false) {
        const query = this.searchInput.value.trim().toLowerCase();
        let matchCount = 0;
        let firstRevealed = null;

        this.cards.forEach((card) => {
            const title = card.getAttribute('data-title') || '';
            const excerpt = card.getAttribute('data-excerpt') || '';
            const tags = (card.getAttribute('data-tags') || '').split(',');

            const matchesQuery = query === '' || title.includes(query) || excerpt.includes(query);
            const matchesTag = this.activeTag === '' || tags.includes(this.activeTag);
            const isMatch = matchesQuery && matchesTag;

            if (isMatch) matchCount += 1;

            const isVisible = isMatch && (this.initialCount === 0 || matchCount <= this.limit);
            if (focusFirstRevealed && isVisible && card.hidden && !firstRevealed) firstRevealed = card;
            card.hidden = !isVisible;
        });

        if (this.showMoreWrap) {
            const hasMore = matchCount > this.limit;
            const waiting = this.loading && !hasMore;

            this.showMoreWrap.hidden = this.initialCount === 0 || (!hasMore && !this.loading);

            if (this.showMoreButton) {
                this.showMoreButton.disabled = waiting;
                this.showMoreButton.setAttribute('aria-busy', String(waiting));
            }
        }

        if (this.emptyState) {
            this.emptyState.hidden = matchCount > 0 || this.loading;
        }

        if (this.resultsCount) {
            if (query === '') {
                this.resultsCount.hidden = true;
            } else {
                const template = this.resultsCount.getAttribute('data-template') || '[count] results';
                this.resultsCount.textContent = template.replace('[count]', String(matchCount));
                this.resultsCount.hidden = false;
            }
        }

        if (firstRevealed) firstRevealed.focus();
    }
}

customElements.define('homediq-blog-filter', HomediqBlogFilter);

function initHomediqBlogCarousel() {
    document.querySelectorAll('[data-carousel]').forEach((el) => {
        if (el.dataset.swiperInitialized) return;
        el.dataset.swiperInitialized = 'true';

        const wrap = el.closest('.homediq-blog-list__products');
        const prevBtn = wrap ? wrap.querySelector('[data-carousel-prev]') : null;
        const nextBtn = wrap ? wrap.querySelector('[data-carousel-next]') : null;

        const swiper = new Swiper(el, {
            slidesPerView: 1.15,
            spaceBetween: 16,
            breakpoints: {
                640: { slidesPerView: 2.3, spaceBetween: 16 },
                1024: { slidesPerView: 3.5, spaceBetween: 16 },
                1200: { slidesPerView: 4.3, spaceBetween: 16 }
            }
        });

        if (prevBtn) prevBtn.addEventListener('click', () => swiper.slidePrev());
        if (nextBtn) nextBtn.addEventListener('click', () => swiper.slideNext());
    });
}

if (typeof Swiper !== 'undefined') {
    initHomediqBlogCarousel();
} else {
    window.addEventListener('load', initHomediqBlogCarousel);
}

/* Cart - Blood draw add-on */
if (!window.__homediqCartAddonBound) {
    window.__homediqCartAddonBound = true;

    var homediqReconcileInFlight = false;

    function homediqSyncPatientFormRequired(form) {
        var isVisible = !form.hidden;
        var dobInput = form.querySelector('[data-cart-patient-dob]');
        var genderInput = form.querySelector('[data-cart-patient-gender]');
        if (dobInput) dobInput.required = isVisible;
        if (genderInput) genderInput.required = isVisible;
    }

    document.querySelectorAll('[data-cart-patient-form]').forEach(homediqSyncPatientFormRequired);

    document.addEventListener('click', function (event) {
        var checkoutBtn = event.target.closest('#cart-drawer-checkout, #checkout');
        if (!checkoutBtn) return;
        document.querySelectorAll('[data-cart-patient-form]').forEach(homediqSyncPatientFormRequired);
    });

    function homediqPostCart(url, payload, errorMessage) {
        return fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        }).then(function (response) {
            return response.json().then(function (data) {
                if (!response.ok) throw new Error(data.description || data.message || errorMessage);
                return data;
            });
        });
    }

    function homediqCompactProperties(properties) {
        var clean = {};
        Object.keys(properties || {}).forEach(function (key) {
            var value = properties[key];
            if (value !== '' && value !== null && value !== undefined) clean[key] = value;
        });
        return clean;
    }

    function homediqRemoveCartLines(keys) {
        var updates = {};
        keys.forEach(function (key) {
            if (key) updates[key] = 0;
        });
        if (Object.keys(updates).length === 0) return Promise.resolve();

        return homediqPostCart('/cart/update.js', { updates: updates }, 'Failed to remove blood draw service');
    }

    function homediqRefreshCartDrawer(options) {
        var action = options && options.action;
        return fetch('/cart.js')
            .then(function (response) { return response.json(); })
            .then(function (cart) {
                var event = new CustomEvent('shopify:cart:lines-update', { bubbles: true });
                event.homediqSynthetic = true;
                if (action) event.action = action;
                event.promise = Promise.resolve({ cart: { totalQuantity: cart.item_count }, detail: {} });
                document.dispatchEvent(event);

                return fetch('/?section_id=cart-drawer-section');
            })
            .then(function (response) {
                if (!response.ok) throw new Error('Failed to refresh cart drawer, status ' + response.status);
                return response.text();
            })
            .then(function (html) {
                var currentInner = document.querySelector('#cart-drawer .cart-drawer__inner');
                if (!currentInner) return;

                var freshDoc = new DOMParser().parseFromString(html, 'text/html');
                var freshInner = freshDoc.querySelector('#cart-drawer .cart-drawer__inner');
                if (!freshInner) return;

                currentInner.innerHTML = freshInner.innerHTML;
                currentInner.classList.toggle('cart-drawer--empty', freshInner.classList.contains('cart-drawer--empty'));
                currentInner.querySelectorAll('[data-cart-patient-form]').forEach(homediqSyncPatientFormRequired);
            });
    }
    window.homediqRefreshCartDrawer = homediqRefreshCartDrawer;

    function homediqRefreshCartPage() {
        var cartWrap = document.querySelector('.homediq-cart-wrap');
        var sectionId = cartWrap ? cartWrap.getAttribute('data-section-id') : null;

        return fetch('/cart.js')
            .then(function (response) { return response.json(); })
            .then(function (cart) {
                var event = new CustomEvent('shopify:cart:lines-update', { bubbles: true });
                event.homediqSynthetic = true;
                event.promise = Promise.resolve({ cart: { totalQuantity: cart.item_count }, detail: {} });
                document.dispatchEvent(event);

                if (!sectionId) throw new Error('Missing cart section id');
                return fetch('/?section_id=' + sectionId);
            })
            .then(function (response) {
                if (!response.ok) throw new Error('Failed to refresh cart, status ' + response.status);
                return response.text();
            })
            .then(function (html) {
                var currentCart = document.querySelector('.homediq-cart');
                if (!currentCart) throw new Error('Missing .homediq-cart element');

                var freshDoc = new DOMParser().parseFromString(html, 'text/html');
                var freshCart = freshDoc.querySelector('.homediq-cart');
                if (!freshCart) throw new Error('Missing .homediq-cart in refreshed section');

                currentCart.innerHTML = freshCart.innerHTML;
                currentCart.querySelectorAll('[data-cart-patient-form]').forEach(homediqSyncPatientFormRequired);
            })
            .catch(function (error) {
                console.error('[homediq cart]', error.message);
                window.location.reload();
            });
    }
    window.homediqRefreshCartPage = homediqRefreshCartPage;

    function homediqAddCartAddon(lineKey, addonVariantId, properties, toggle) {
        var card = toggle ? toggle.closest('.homediq-cart__card') : null;
        var scope = card || document;
        var otherLineKeysToRemove = [];
        scope.querySelectorAll('[data-homediq-blood-draw-toggle][data-line-key="' + lineKey + '"]').forEach(function (otherToggle) {
            if (otherToggle === toggle) return;
            if (otherToggle.getAttribute('aria-checked') !== 'true') return;
            (otherToggle.getAttribute('data-addon-line-keys') || '')
                .split(',')
                .map(function (k) { return k.trim(); })
                .filter(Boolean)
                .forEach(function (k) { otherLineKeysToRemove.push(k); });
        });

        var removeOtherPromise = otherLineKeysToRemove.length > 0
            ? homediqRemoveCartLines(otherLineKeysToRemove)
            : Promise.resolve();

        return removeOtherPromise
            .then(function () {
                return homediqPostCart('/cart/add.js', {
                    id: addonVariantId,
                    quantity: 1,
                    properties: properties
                }, 'Failed to add blood draw service');
            });
    }

    function homediqSellingPlanId(item) {
        return item.selling_plan_allocation && item.selling_plan_allocation.selling_plan
            ? item.selling_plan_allocation.selling_plan.id
            : null;
    }

    function homediqAddBloodDrawService(card, parentKey, addonVariantId, patient) {
        var tassoKeysToRemove = [];
        (card || document).querySelectorAll('[data-homediq-blood-draw-toggle]:not([data-requires-patient-form])').forEach(function (otherToggle) {
            if (otherToggle.getAttribute('aria-checked') !== 'true') return;
            (otherToggle.getAttribute('data-addon-line-keys') || '')
                .split(',')
                .map(function (k) { return k.trim(); })
                .filter(Boolean)
                .forEach(function (k) { tassoKeysToRemove.push(k); });
        });

        return homediqRemoveCartLines(tassoKeysToRemove)
            .then(function () { return fetch('/cart.js').then(function (response) { return response.json(); }); })
            .then(function (cart) {
                var parent = null;
                cart.items.forEach(function (item) {
                    if (item.key === parentKey) parent = item;
                });
                if (!parent) throw new Error('Cart line not found');

                var parentProperties = Object.assign({}, parent.properties || {});
                var linkId = parentProperties['_prikpost_link'];
                var hasLink = Boolean(linkId);
                if (!hasLink) linkId = '_prikpost_link_' + parent.product_id + '_' + Date.now() + '_0';
                parentProperties['_prikpost'] = 'true';
                parentProperties['_prikpost_variant_id'] = String(addonVariantId);
                parentProperties['_prikpost_link'] = linkId;

                var sellingPlanId = homediqSellingPlanId(parent);

                var serviceItem = {
                    id: addonVariantId,
                    quantity: hasLink ? parent.quantity : 1,
                    properties: homediqCompactProperties(Object.assign({ '_prikpost_link': linkId, '_prikpost_service': 'true' }, patient))
                };

                if (hasLink) {
                    return homediqPostCart('/cart/add.js', { items: [serviceItem] }, 'Failed to add blood draw service');
                }

                var linkedMain = { id: parent.variant_id, quantity: 1, properties: parentProperties };
                var originalUnit = { id: parent.variant_id, quantity: 1, properties: Object.assign({}, parent.properties || {}) };
                if (sellingPlanId) {
                    linkedMain.selling_plan = sellingPlanId;
                    originalUnit.selling_plan = sellingPlanId;
                }

                var takeOutOneUnit = parent.quantity === 1
                    ? homediqRemoveCartLines([parentKey])
                    : homediqPostCart('/cart/change.js', { id: parentKey, quantity: parent.quantity - 1 }, 'Failed to update cart line');

                return takeOutOneUnit.then(function () {
                    return homediqPostCart('/cart/add.js', { items: [linkedMain, serviceItem] }, 'Failed to add blood draw service')
                        .catch(function (error) {
                            return homediqPostCart('/cart/add.js', originalUnit, 'Failed to restore cart line')
                                .catch(function () {})
                                .then(function () { throw error; });
                        });
                });
            });
    }

    function homediqRemoveBloodDrawService(card, serviceKeys) {
        var removeButton = card ? card.querySelector('[data-homediq-remove-line]') : null;
        var parentKey = removeButton ? removeButton.getAttribute('data-line-item-key') : null;
        if (!parentKey) return homediqRemoveCartLines(serviceKeys);

        return fetch('/cart.js')
            .then(function (response) { return response.json(); })
            .then(function (cart) {
                var parent = null;
                cart.items.forEach(function (item) {
                    if (item.key === parentKey) parent = item;
                });
                if (!parent || !parent.properties || !parent.properties['_prikpost_link']) {
                    return homediqRemoveCartLines(serviceKeys);
                }

                var plainProperties = Object.assign({}, parent.properties);
                delete plainProperties['_prikpost'];
                delete plainProperties['_prikpost_variant_id'];
                delete plainProperties['_prikpost_link'];

                var plainLine = { id: parent.variant_id, quantity: parent.quantity, properties: plainProperties };
                var originalLine = { id: parent.variant_id, quantity: parent.quantity, properties: Object.assign({}, parent.properties) };
                var sellingPlanId = homediqSellingPlanId(parent);
                if (sellingPlanId) {
                    plainLine.selling_plan = sellingPlanId;
                    originalLine.selling_plan = sellingPlanId;
                }

                return homediqRemoveCartLines(serviceKeys.concat([parentKey])).then(function () {
                    return homediqPostCart('/cart/add.js', plainLine, 'Failed to update cart line')
                        .catch(function (error) {
                            return homediqPostCart('/cart/add.js', originalLine, 'Failed to restore cart line')
                                .catch(function () {})
                                .then(function () { throw error; });
                        });
                });
            });
    }

    function homediqReconcileBloodDrawLines() {
        if (homediqReconcileInFlight) return Promise.resolve(false);
        homediqReconcileInFlight = true;

        return fetch('/cart.js')
            .then(function (response) { return response.json(); })
            .then(function (cart) {
                var mainByLink = {};
                var services = [];

                cart.items.forEach(function (item) {
                    var properties = item.properties || {};
                    var link = properties['_prikpost_link'];
                    if (!link) return;
                    if (properties['_prikpost_service'] === 'true') {
                        services.push(item);
                    } else {
                        mainByLink[link] = item;
                    }
                });

                var updates = {};
                services.forEach(function (service) {
                    var main = mainByLink[service.properties['_prikpost_link']];
                    if (!main) {
                        updates[service.key] = 0;
                    } else if (service.quantity !== main.quantity) {
                        updates[service.key] = main.quantity;
                    }
                });

                if (Object.keys(updates).length === 0) return false;

                return homediqPostCart('/cart/update.js', { updates: updates }, 'Failed to sync blood draw service').then(function () {
                    return true;
                });
            })
            .catch(function (error) {
                console.error('[homediq blood draw]', error.message);
                return false;
            })
            .then(function (changed) {
                homediqReconcileInFlight = false;
                return changed;
            });
    }

    document.addEventListener('shopify:cart:lines-update', function (event) {
        if (event.homediqSynthetic) return;
        if (!event.promise || typeof event.promise.then !== 'function') return;

        event.promise
            .then(function () { return homediqReconcileBloodDrawLines(); })
            .then(function (changed) {
                if (!changed) return;
                var refreshes = [];
                if (document.querySelector('#cart-drawer .cart-drawer__inner')) refreshes.push(homediqRefreshCartDrawer());
                if (document.querySelector('.homediq-cart-wrap')) refreshes.push(homediqRefreshCartPage());
                return Promise.all(refreshes);
            })
            .catch(function () {});
    });

    document.addEventListener('click', function (event) {
        var toggle = event.target.closest('[data-homediq-blood-draw-toggle]');
        if (!toggle || toggle.disabled) return;

        var lineKey = toggle.getAttribute('data-line-key');
        var addonVariantId = toggle.getAttribute('data-addon-variant-id');
        var addonLineKeys = (toggle.getAttribute('data-addon-line-keys') || '')
            .split(',')
            .map(function (k) { return k.trim(); })
            .filter(Boolean);
        var isActive = toggle.getAttribute('aria-checked') === 'true';
        var isDrawerToggle = Boolean(toggle.closest('#cart-drawer'));

        if (!isActive && toggle.hasAttribute('data-requires-patient-form')) {
            var addonWrap = toggle.closest('.homediq-cart__addon-wrap');
            var form = addonWrap ? addonWrap.querySelector('[data-cart-patient-form]') : null;
            if (form) {
                form.hidden = false;
                homediqSyncPatientFormRequired(form);
                var focusTarget = form.querySelector('input, select, button');
                if (focusTarget) focusTarget.focus();
            }
            return;
        }

        toggle.disabled = true;
        toggle.classList.add('is-loading');

        function reset(errorMessage) {
            toggle.disabled = false;
            toggle.classList.remove('is-loading');
            if (errorMessage) console.error('[homediq blood draw]', errorMessage);
        }

        if (isActive) {
            var removePromise = toggle.hasAttribute('data-requires-patient-form')
                ? homediqRemoveBloodDrawService(toggle.closest('.homediq-cart__card'), addonLineKeys)
                : homediqRemoveCartLines(addonLineKeys);

            removePromise
                .then(function () {
                    if (isDrawerToggle) return homediqRefreshCartDrawer();
                    return homediqRefreshCartPage();
                })
                .catch(function (error) {
                    reset(error.message);
                });
        } else {
            homediqAddCartAddon(lineKey, addonVariantId, { _blood_draw_for: lineKey }, toggle)
                .then(function () {
                    if (isDrawerToggle) return homediqRefreshCartDrawer();
                    return homediqRefreshCartPage();
                })
                .catch(function (error) {
                    reset(error.message);
                });
        }
    });

    document.addEventListener('click', function (event) {
        var cancelBtn = event.target.closest('[data-cart-patient-cancel]');
        if (!cancelBtn) return;

        var form = cancelBtn.closest('[data-cart-patient-form]');
        if (form) {
            form.hidden = true;
            homediqSyncPatientFormRequired(form);
        }
    });

    document.addEventListener('click', function (event) {
        var forOption = event.target.closest('[data-patient-for-option]');
        var toggleGroup = forOption ? forOption.closest('[data-cart-patient-for-toggle]') : null;
        if (!toggleGroup) return;

        var form = forOption.closest('[data-cart-patient-form]');
        var nameFields = form ? form.querySelector('[data-cart-patient-name-fields]') : null;
        var firstNameInput = form ? form.querySelector('[data-cart-patient-first-name]') : null;
        var lastNameInput = form ? form.querySelector('[data-cart-patient-last-name]') : null;

        toggleGroup.querySelectorAll('[data-patient-for-option]').forEach(function (opt) {
            var isMatch = opt === forOption;
            opt.classList.toggle('is-active', isMatch);
            opt.setAttribute('aria-checked', isMatch ? 'true' : 'false');
        });

        var isOther = forOption.getAttribute('data-patient-for-option') === 'other';
        if (nameFields) nameFields.hidden = !isOther;
        if (firstNameInput) firstNameInput.required = isOther;
        if (lastNameInput) lastNameInput.required = isOther;
    });

    document.addEventListener('click', function (event) {
        var confirmBtn = event.target.closest('[data-cart-patient-confirm]');
        if (!confirmBtn || confirmBtn.disabled) return;

        var form = confirmBtn.closest('[data-cart-patient-form]');
        if (!form) return;

        var parentKey = confirmBtn.getAttribute('data-parent-key');
        var addonVariantId = confirmBtn.getAttribute('data-addon-variant-id');
        var card = confirmBtn.closest('.homediq-cart__card');
        var isDrawerConfirm = Boolean(confirmBtn.closest('#cart-drawer'));

        var activeForOption = form.querySelector('[data-patient-for-option].is-active');
        var isOther = activeForOption ? activeForOption.getAttribute('data-patient-for-option') === 'other' : false;

        var firstNameInput = form.querySelector('[data-cart-patient-first-name]');
        var lastNameInput = form.querySelector('[data-cart-patient-last-name]');
        var dobInput = form.querySelector('[data-cart-patient-dob]');
        var genderInput = form.querySelector('[data-cart-patient-gender]');

        var fieldsToValidate = [dobInput, genderInput];
        if (isOther) fieldsToValidate.push(firstNameInput, lastNameInput);

        var isValid = fieldsToValidate.every(function (field) { return !field || field.reportValidity(); });
        if (!isValid) return;

        var patient = {
            '_for_other': isOther ? 'true' : 'false',
            'birth_date': dobInput ? dobInput.value : '',
            'sex': genderInput ? genderInput.value.toLowerCase() : ''
        };
        if (isOther) {
            patient['first_name'] = firstNameInput ? firstNameInput.value.trim() : '';
            patient['last_name'] = lastNameInput ? lastNameInput.value.trim() : '';
        }

        confirmBtn.disabled = true;
        confirmBtn.classList.add('is-loading');

        homediqAddBloodDrawService(card, parentKey, addonVariantId, patient)
            .then(function () {
                if (isDrawerConfirm) return homediqRefreshCartDrawer();
                return homediqRefreshCartPage();
            })
            .catch(function (error) {
                confirmBtn.disabled = false;
                confirmBtn.classList.remove('is-loading');
                console.error('[homediq blood draw]', error.message);
            });
    });

    document.addEventListener('click', function (event) {
        var editBtn = event.target.closest('[data-cart-addon-dob-edit]');
        if (!editBtn) return;

        var wrap = editBtn.closest('[data-cart-addon-dob-wrap]');
        if (!wrap) return;

        var display = wrap.querySelector('[data-cart-addon-dob-display]');
        var dobForm = wrap.querySelector('[data-cart-addon-dob-form]');
        if (display) display.hidden = true;
        if (dobForm) {
            dobForm.hidden = false;
            var dobInput = dobForm.querySelector('[data-cart-addon-dob-input]');
            if (dobInput) dobInput.focus();
        }
    });

    document.addEventListener('click', function (event) {
        var cancelBtn = event.target.closest('[data-cart-addon-dob-cancel]');
        if (!cancelBtn) return;

        var wrap = cancelBtn.closest('[data-cart-addon-dob-wrap]');
        if (!wrap) return;

        var display = wrap.querySelector('[data-cart-addon-dob-display]');
        var dobForm = wrap.querySelector('[data-cart-addon-dob-form]');
        if (dobForm) dobForm.hidden = true;
        if (display) display.hidden = false;
    });

    document.addEventListener('click', function (event) {
        var saveBtn = event.target.closest('[data-cart-addon-dob-save]');
        if (!saveBtn || saveBtn.disabled) return;

        var wrap = saveBtn.closest('[data-cart-addon-dob-wrap]');
        if (!wrap) return;

        var dobInput = wrap.querySelector('[data-cart-addon-dob-input]');
        if (dobInput && !dobInput.reportValidity()) return;

        var lineKey = wrap.getAttribute('data-line-key');
        var addonVariantId = wrap.getAttribute('data-addon-variant-id');
        var addonQuantity = parseInt(wrap.getAttribute('data-addon-quantity'), 10) || 1;
        var currentProperties = {};
        try {
            currentProperties = JSON.parse(wrap.getAttribute('data-current-properties') || '{}');
        } catch (error) {
            currentProperties = {};
        }

        var properties = homediqCompactProperties(Object.assign({}, currentProperties, {
            'birth_date': dobInput ? dobInput.value : ''
        }));
        var isDrawerSave = Boolean(wrap.closest('#cart-drawer'));

        saveBtn.disabled = true;
        saveBtn.classList.add('is-loading');

        homediqRemoveCartLines([lineKey])
            .then(function () {
                return homediqPostCart('/cart/add.js', { id: addonVariantId, quantity: addonQuantity, properties: properties }, 'Failed to update date of birth');
            })
            .then(function () {
                if (isDrawerSave) return homediqRefreshCartDrawer();
                return homediqRefreshCartPage();
            })
            .catch(function (error) {
                saveBtn.disabled = false;
                saveBtn.classList.remove('is-loading');
                console.error('[homediq blood draw]', error.message);
            });
    });

    document.addEventListener('click', function (event) {
        var removeButton = event.target.closest('[data-homediq-remove-line]');
        if (!removeButton) return;

        var addonLineKeys = (removeButton.getAttribute('data-blood-draw-line-keys') || '')
            .split(',')
            .map(function (k) { return k.trim(); })
            .filter(Boolean);

        var isDrawerButton = removeButton.classList.contains('homediq-cart-drawer__remove');

        if (!isDrawerButton && addonLineKeys.length === 0) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        var ownKey = removeButton.getAttribute('data-line-item-key');
        var allKeys = ownKey ? [ownKey].concat(addonLineKeys) : addonLineKeys;

        homediqRemoveCartLines(allKeys)
            .then(function () {
                if (isDrawerButton) return homediqRefreshCartDrawer();
                return homediqRefreshCartPage();
            })
            .catch(function (error) {
                console.error('[homediq cart remove]', error.message);
            });
    }, true);
}

/* Blog article - auto table of contents */
(function () {
    var prose = document.querySelector('.homediq-blog-article__prose');
    if (!prose) return;

    var headings = prose.querySelectorAll('h2');
    if (headings.length === 0) return;

    var usedSlugs = {};
    var items = [];

    headings.forEach(function (heading, index) {
        var text = heading.textContent.trim();
        if (!text) return;

        var slug = heading.id;
        if (!slug) {
            slug = text
                .toLowerCase()
                .replace(/[^a-z0-9\s-]/g, '')
                .trim()
                .replace(/\s+/g, '-');
            if (!slug) slug = 'section-' + (index + 1);

            var uniqueSlug = slug;
            var counter = 2;
            while (usedSlugs[uniqueSlug] || document.getElementById(uniqueSlug)) {
                uniqueSlug = slug + '-' + counter;
                counter += 1;
            }
            slug = uniqueSlug;
            heading.id = slug;
        }
        usedSlugs[slug] = true;

        items.push({ slug: slug, text: text });
    });

    if (items.length === 0) return;

    var listHtml = items
        .map(function (item, index) {
            return (
                '<li><a href="#' +
                item.slug +
                '" class="homediq-blog-article__toc-link"><span class="homediq-blog-article__toc-number">' +
                (index + 1) +
                '.</span>' +
                item.text +
                '</a></li>'
            );
        })
        .join('');

    document.querySelectorAll('[data-homediq-toc-list]').forEach(function (list) {
        list.innerHTML = listHtml;
    });

    document.querySelectorAll('[data-homediq-toc-sidebar], [data-homediq-toc-mobile]').forEach(function (el) {
        el.hidden = false;
    });

    if (typeof IntersectionObserver === 'undefined') return;

    var setActive = function (id) {
        document.querySelectorAll('[data-homediq-toc-list] a').forEach(function (link) {
            link.classList.toggle('is-active', link.getAttribute('href') === '#' + id);
        });
    };

    var observer = new IntersectionObserver(
        function (entries) {
            entries.forEach(function (entry) {
                if (entry.isIntersecting) {
                    setActive(entry.target.id);
                }
            });
        },
        { rootMargin: '-100px 0px -70% 0px', threshold: 0 }
    );

    headings.forEach(function (heading) {
        observer.observe(heading);
    });
})();

/* Blog article - attach a trailing paragraph to its preceding table as a caption */
(function () {
    var prose = document.querySelector('.homediq-blog-article__prose');
    if (!prose) return;

    var tables = prose.querySelectorAll('table');
    tables.forEach(function (table) {
        var topNode = table;
        while (topNode.parentElement && topNode.parentElement !== prose) {
            topNode = topNode.parentElement;
        }

        var next = topNode.nextElementSibling;
        if (!next || next.tagName !== 'P' || !next.textContent.trim()) return;

        var wrapper = document.createElement('div');
        wrapper.className = 'homediq-blog-article__table-wrap';

        var caption = document.createElement('div');
        caption.className = 'homediq-blog-article__table-caption';
        caption.innerHTML = next.innerHTML;

        topNode.parentNode.insertBefore(wrapper, topNode);
        wrapper.appendChild(topNode);
        wrapper.appendChild(caption);
        next.remove();
    });
})();

/* Blog article - structure references parsed from raw citation HTML */
(function () {
    var items = document.querySelectorAll('[data-homediq-reference-raw]');
    if (items.length === 0) return;

    function clean(str) {
        return str
            .replace(/&nbsp;/g, ' ')
            .replace(/\s+/g, ' ')
            .replace(/^[.,\s]+/, '')
            .replace(/[.,\s]+$/, '')
            .trim();
    }

    function parseCitation(raw) {
        var emMatch = raw.match(/<em>([\s\S]*?)<\/em>/);
        if (!emMatch) return null;

        var title = clean(emMatch[1]).replace(/\.$/, '');
        if (!title) return null;

        var prefix = raw.slice(0, emMatch.index);
        var suffix = raw.slice(emMatch.index + emMatch[0].length);

        var yearMatch = prefix.match(/\((\d{4})\)/);
        var year = yearMatch ? yearMatch[1] : '';

        var urlMatch = suffix.match(/https?:\/\/\S+/);
        var url = urlMatch ? urlMatch[0].replace(/[.,;)]+$/, '') : '';

        var publisher = clean(suffix.split(/Retrieved/)[0]);
        if (!publisher) {
            publisher = clean(prefix.replace(/\(\d{4}\)\.?/, ''));
        }

        var source = publisher;
        if (year) source += (source ? ' ' : '') + '(' + year + ')';

        return { title: title, source: source, url: url };
    }

    items.forEach(function (li) {
        var raw = li.getAttribute('data-homediq-reference-raw');
        var body = li.querySelector('[data-homediq-reference-fallback]');
        if (!raw || !body) return;

        var parsed = parseCitation(raw);
        if (!parsed) return;

        body.innerHTML = '';
        body.removeAttribute('data-homediq-reference-fallback');

        var titleSpan = document.createElement('span');
        titleSpan.className = 'homediq-blog-article__source-title';
        titleSpan.textContent = parsed.title;
        body.appendChild(titleSpan);

        if (parsed.source) {
            var sourceSpan = document.createElement('span');
            sourceSpan.className = 'homediq-blog-article__source-meta';
            sourceSpan.textContent = ', ' + parsed.source;
            body.appendChild(sourceSpan);
        }

        if (parsed.url) {
            var link = document.createElement('a');
            link.href = parsed.url;
            link.target = '_blank';
            link.rel = 'noopener noreferrer';
            link.className = 'homediq-blog-article__source-link';
            link.setAttribute('aria-label', parsed.title);
            link.innerHTML = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 3h6v6"></path><path d="M10 14 21 3"></path><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path></svg>';
            body.appendChild(link);
        }
    });
})();

/* Blog article - smooth FAQ accordion, only one item open at a time */
(function () {
    var items = document.querySelectorAll('.homediq-blog-article__faq-item');
    if (items.length === 0) return;

    var controllers = [];

    items.forEach(function (details) {
        var summary = details.querySelector('summary');
        var content = details.querySelector('.homediq-blog-article__faq-content');
        if (!summary || !content) return;
        if (typeof details.animate !== 'function') return;

        var animation = null;
        var isClosing = false;
        var isExpanding = false;

        function onAnimationFinish(open) {
            details.open = open;
            animation = null;
            isClosing = false;
            isExpanding = false;
            details.style.height = '';
            details.style.overflow = '';
        }

        function shrink() {
            isClosing = true;
            var startHeight = details.offsetHeight + 'px';
            var endHeight = summary.offsetHeight + 'px';
            if (animation) animation.cancel();
            animation = details.animate(
                { height: [startHeight, endHeight] },
                { duration: 250, easing: 'ease-out' }
            );
            animation.onfinish = function () { onAnimationFinish(false); };
            animation.oncancel = function () { isClosing = false; };
        }

        function expand() {
            isExpanding = true;
            var startHeight = details.offsetHeight + 'px';
            var endHeight = summary.offsetHeight + content.offsetHeight + 'px';
            if (animation) animation.cancel();
            animation = details.animate(
                { height: [startHeight, endHeight] },
                { duration: 250, easing: 'ease-out' }
            );
            animation.onfinish = function () { onAnimationFinish(true); };
            animation.oncancel = function () { isExpanding = false; };
        }

        function open() {
            details.style.height = details.offsetHeight + 'px';
            details.open = true;
            window.requestAnimationFrame(function () { expand(); });
        }

        var controller = {
            isOpen: function () {
                return !isClosing && (isExpanding || details.open);
            },
            close: function () {
                if (isClosing || !details.open) return;
                details.style.overflow = 'hidden';
                shrink();
            }
        };
        controllers.push(controller);

        summary.addEventListener('click', function (event) {
            event.preventDefault();
            details.style.overflow = 'hidden';
            if (isClosing || !details.open) {
                controllers.forEach(function (other) {
                    if (other !== controller && other.isOpen()) {
                        other.close();
                    }
                });
                open();
            } else if (isExpanding || details.open) {
                shrink();
            }
        });
    });
})();

document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-homediq-feedback-value]');
    if (!button) return;

    var card = button.closest('[data-homediq-feedback]');
    if (!card) return;

    var initial = card.querySelector('[data-homediq-feedback-initial]');
    var thanks = card.querySelector('[data-homediq-feedback-thanks]');

    if (initial) initial.hidden = true;
    if (thanks) thanks.hidden = false;
});

/* Blog article - reviewer modal */
document.addEventListener('click', function (event) {
    var modal = document.querySelector('[data-homediq-reviewer-modal]');
    if (!modal) return;

    if (event.target.closest('[data-homediq-reviewer-open]')) {
        modal.hidden = false;
        document.body.style.overflow = 'hidden';
        return;
    }

    if (event.target.closest('[data-homediq-reviewer-modal-close]')) {
        modal.hidden = true;
        document.body.style.overflow = '';
    }
});

document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;
    var modal = document.querySelector('[data-homediq-reviewer-modal]');
    if (modal && !modal.hidden) {
        modal.hidden = true;
        document.body.style.overflow = '';
    }
});

/* Product FAQ - smooth accordion, only one item open at a time */
(function () {
    var items = document.querySelectorAll('[data-homediq-product-faq-item]');
    if (items.length === 0) return;

    var controllers = [];

    items.forEach(function (details) {
        var summary = details.querySelector('summary');
        var content = details.querySelector('.homediq-product-faq__content');
        if (!summary || !content) return;
        if (typeof details.animate !== 'function') return;

        var animation = null;
        var isClosing = false;
        var isExpanding = false;

        function onAnimationFinish(open) {
            details.open = open;
            animation = null;
            isClosing = false;
            isExpanding = false;
            details.style.height = '';
            details.style.overflow = '';
        }

        function shrink() {
            isClosing = true;
            var startHeight = details.offsetHeight + 'px';
            var endHeight = summary.offsetHeight + 'px';
            if (animation) animation.cancel();
            animation = details.animate(
                { height: [startHeight, endHeight] },
                { duration: 250, easing: 'ease-out' }
            );
            animation.onfinish = function () { onAnimationFinish(false); };
            animation.oncancel = function () { isClosing = false; };
        }

        function expand() {
            isExpanding = true;
            var startHeight = details.offsetHeight + 'px';
            var endHeight = summary.offsetHeight + content.offsetHeight + 'px';
            if (animation) animation.cancel();
            animation = details.animate(
                { height: [startHeight, endHeight] },
                { duration: 250, easing: 'ease-out' }
            );
            animation.onfinish = function () { onAnimationFinish(true); };
            animation.oncancel = function () { isExpanding = false; };
        }

        function open() {
            details.style.height = details.offsetHeight + 'px';
            details.open = true;
            window.requestAnimationFrame(function () { expand(); });
        }

        var controller = {
            isOpen: function () {
                return !isClosing && (isExpanding || details.open);
            },
            close: function () {
                if (isClosing || !details.open) return;
                details.style.overflow = 'hidden';
                shrink();
            }
        };
        controllers.push(controller);

        summary.addEventListener('click', function (event) {
            event.preventDefault();
            details.style.overflow = 'hidden';
            if (isClosing || !details.open) {
                controllers.forEach(function (other) {
                    if (other !== controller && other.isOpen()) {
                        other.close();
                    }
                });
                open();
            } else if (isExpanding || details.open) {
                shrink();
            }
        });
    });
})();

document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-homediq-faq-show-more]');
    if (!button) return;

    var wrap = button.closest('.homediq-product-faq-wrap');
    if (!wrap) return;

    wrap.querySelectorAll('[data-homediq-faq-extra]').forEach(function (item) {
        item.hidden = false;
    });

    var showMoreWrap = button.closest('.homediq-product-faq__show-more-wrap');
    if (showMoreWrap) showMoreWrap.hidden = true;
});

/* Product reviews - Swiper carousel */
function initHomediqProductReviewsCarousel() {
    document.querySelectorAll('.homediq-product-reviews__swiper[data-reviews-carousel]').forEach(function (el) {
        if (el.dataset.reviewsSwiperInitialized) return;
        el.dataset.reviewsSwiperInitialized = 'true';

        var wrap = el.closest('.homediq-product-reviews');
        var prevBtn = wrap ? wrap.querySelector('[data-reviews-carousel-prev]') : null;
        var nextBtn = wrap ? wrap.querySelector('[data-reviews-carousel-next]') : null;

        var swiper = new Swiper(el, {
            slidesPerView: 'auto',
            spaceBetween: 20,
            freeMode: true
        });

        if (prevBtn) prevBtn.addEventListener('click', function () { swiper.slidePrev(); });
        if (nextBtn) nextBtn.addEventListener('click', function () { swiper.slideNext(); });
    });
}

if (typeof Swiper !== 'undefined') {
    initHomediqProductReviewsCarousel();
} else {
    window.addEventListener('load', initHomediqProductReviewsCarousel);
}

/* Homediq product tabs */
class HomediqProductTabs extends HTMLElement {
    connectedCallback() {
        this.triggers = this.querySelectorAll('[data-tab-trigger]');
        this.panels = this.querySelectorAll('[data-tab-panel]');

        this.triggers.forEach((trigger) => {
            trigger.addEventListener('click', () => {
                const target = trigger.getAttribute('data-tab-trigger');

                this.triggers.forEach((t) => {
                    const isMatch = t === trigger;
                    t.classList.toggle('is-active', isMatch);
                    t.setAttribute('aria-selected', isMatch ? 'true' : 'false');
                });

                this.panels.forEach((panel) => {
                    const isMatch = panel.getAttribute('data-tab-panel') === target;
                    panel.classList.toggle('is-active', isMatch);
                    panel.hidden = !isMatch;
                });
            });
        });

        this.querySelectorAll('[data-biomarker-trigger]').forEach((trigger) => {
            trigger.addEventListener('click', (event) => {
                const modalId = trigger.getAttribute('data-modal-target');
                const modal = this.querySelector('#' + modalId);
                if (!modal) return;
                event.preventDefault();
                modal.hidden = false;
            });
        });

        this.querySelectorAll('[data-biomarker-modal-close]').forEach((closeEl) => {
            closeEl.addEventListener('click', () => {
                const modal = closeEl.closest('[data-biomarker-modal]');
                if (modal) modal.hidden = true;
            });
        });

        this.querySelectorAll('[data-symptoms-item-trigger]').forEach((trigger) => {
            trigger.addEventListener('click', () => {
                const panel = this.querySelector('#' + trigger.getAttribute('aria-controls'));
                if (!panel) return;
                const isOpen = trigger.getAttribute('aria-expanded') === 'true';
                trigger.setAttribute('aria-expanded', String(!isOpen));
                panel.hidden = isOpen;
            });
        });
        
        this.querySelectorAll('[data-symptoms-expand-all]').forEach((btn) => {
            btn.addEventListener('click', () => {
                const wrap = btn.closest('[data-symptoms-accordion]');
                if (!wrap) return;
                wrap.querySelectorAll('[data-symptoms-item-trigger]').forEach((trigger) => {
                    trigger.setAttribute('aria-expanded', 'true');
                    const panel = wrap.querySelector('#' + trigger.getAttribute('aria-controls'));
                    if (panel) panel.hidden = false;
                });
            });
        });
        
        this.querySelectorAll('[data-symptoms-collapse-all]').forEach((btn) => {
            btn.addEventListener('click', () => {
                const wrap = btn.closest('[data-symptoms-accordion]');
                if (!wrap) return;
                wrap.querySelectorAll('[data-symptoms-item-trigger]').forEach((trigger) => {
                    trigger.setAttribute('aria-expanded', 'false');
                    const panel = wrap.querySelector('#' + trigger.getAttribute('aria-controls'));
                    if (panel) panel.hidden = true;
                });
            });
        });

        document.addEventListener('keydown', (event) => {
            if (event.key !== 'Escape') return;
            this.querySelectorAll('[data-biomarker-modal]:not([hidden])').forEach((modal) => {
                modal.hidden = true;
            });
        });
    }
}

customElements.define('homediq-product-tabs', HomediqProductTabs);

/* Homediq product tabs scroll */
function initHomediqTablistScrollbar(root) {
    var scrollEl = root.querySelector('[data-tablist-scroll]');
    var track = root.querySelector('[data-tablist-scrollbar]');
    var thumb = root.querySelector('[data-tablist-scrollbar-thumb]');
    if (!scrollEl || !track || !thumb) return;

    var isDragging = false;
    var dragStartX = 0;
    var startScrollLeft = 0;

    function updateThumb() {
        var scrollWidth = scrollEl.scrollWidth;
        var clientWidth = scrollEl.clientWidth;

        if (scrollWidth <= clientWidth + 1) {
            track.hidden = true;
            return;
        }
        track.hidden = false;

        var trackWidth = track.clientWidth;
        var thumbWidth = Math.max((clientWidth / scrollWidth) * trackWidth, 24);
        var maxThumbLeft = trackWidth - thumbWidth;
        var maxScrollLeft = scrollWidth - clientWidth;
        var scrollRatio = maxScrollLeft > 0 ? scrollEl.scrollLeft / maxScrollLeft : 0;

        thumb.style.width = thumbWidth + 'px';
        thumb.style.transform = 'translateX(' + (scrollRatio * maxThumbLeft) + 'px)';
    }

    scrollEl.addEventListener('scroll', updateThumb);
    window.addEventListener('resize', updateThumb);
    window.addEventListener('load', updateThumb);
    if (typeof ResizeObserver !== 'undefined') {
        new ResizeObserver(updateThumb).observe(scrollEl);
    }
    updateThumb();

    thumb.addEventListener('pointerdown', function (event) {
        isDragging = true;
        dragStartX = event.clientX;
        startScrollLeft = scrollEl.scrollLeft;
        thumb.classList.add('is-dragging');
        thumb.setPointerCapture(event.pointerId);
    });

    thumb.addEventListener('pointermove', function (event) {
        if (!isDragging) return;
        var trackWidth = track.clientWidth;
        var thumbWidth = thumb.offsetWidth;
        var maxThumbLeft = trackWidth - thumbWidth;
        var maxScrollLeft = scrollEl.scrollWidth - scrollEl.clientWidth;
        if (maxThumbLeft <= 0) return;

        var deltaX = event.clientX - dragStartX;
        scrollEl.scrollLeft = startScrollLeft + (deltaX / maxThumbLeft) * maxScrollLeft;
    });

    function stopDragging() {
        isDragging = false;
        thumb.classList.remove('is-dragging');
    }
    thumb.addEventListener('pointerup', stopDragging);
    thumb.addEventListener('pointercancel', stopDragging);

    track.addEventListener('pointerdown', function (event) {
        if (event.target === thumb) return;
        var trackRect = track.getBoundingClientRect();
        var thumbWidth = thumb.offsetWidth;
        var trackWidth = track.clientWidth;
        var maxThumbLeft = trackWidth - thumbWidth;
        var maxScrollLeft = scrollEl.scrollWidth - scrollEl.clientWidth;
        if (maxThumbLeft <= 0) return;

        var clickX = event.clientX - trackRect.left;
        var targetThumbLeft = Math.min(Math.max(clickX - thumbWidth / 2, 0), maxThumbLeft);
        scrollEl.scrollLeft = (targetThumbLeft / maxThumbLeft) * maxScrollLeft;
    });
}

document.querySelectorAll('homediq-product-tabs').forEach(initHomediqTablistScrollbar);

/* Homediq product main */
class HomediqProductMain extends HTMLElement {
    connectedCallback() {
        this.initGallery();
        this.initMobileGallery();
        this.initReviews();
        this.initBiomarkerModals();
        this.initVariantPicker();
        this.initCollectionMethod();
        this.initSubscriptionPicker();
        this.initAddToCartProxy();
    }

    initGallery() {
        this.viewerItems = Array.from(this.querySelectorAll('[data-gallery-item]'));
        this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
        if (!this.viewerItems.length) return;

        this.activeIndex = 0;
        this.viewerItems.forEach((item, i) => {
            if (item.classList.contains('is-active')) this.activeIndex = i;
        });

        const isVisible = (index) => {
            const item = this.viewerItems[index];
            return !!item && !item.classList.contains('is-variant-hidden');
        };

        const visibleIndexes = () => this.viewerItems.map((_, i) => i).filter(isVisible);

        const showIndex = (index) => {
            const total = this.viewerItems.length;
            this.activeIndex = ((index % total) + total) % total;

            this.viewerItems.forEach((item, i) => {
                item.classList.toggle('is-active', i === this.activeIndex);
            });
            this.thumbs.forEach((thumb, i) => {
                thumb.classList.toggle('is-active', i === this.activeIndex);
            });
        };

        const stepIndex = (direction) => {
            const visible = visibleIndexes();
            if (visible.length === 0) return;
            const currentPos = visible.indexOf(this.activeIndex);
            const total = visible.length;
            const nextPos = (((currentPos === -1 ? 0 : currentPos) + direction) % total + total) % total;
            showIndex(visible[nextPos]);
        };

        const prevBtn = this.querySelector('[data-gallery-prev]');
        const nextBtn = this.querySelector('[data-gallery-next]');
        if (prevBtn) prevBtn.addEventListener('click', () => stepIndex(-1));
        if (nextBtn) nextBtn.addEventListener('click', () => stepIndex(1));

        this.thumbs.forEach((thumb, i) => {
            thumb.addEventListener('click', () => showIndex(i));
        });

        this.updateVariantMedia = (variantId) => {
            const idStr = String(variantId);

            this.querySelectorAll('[data-variant-media]').forEach((el) => {
                const matches = el.matches('[data-variant-media~="' + idStr + '"]');
                el.classList.toggle('is-variant-hidden', !matches);
            });

            const ownViewerItem = this.querySelector('[data-gallery-item][data-variant-media~="' + idStr + '"]');
            if (ownViewerItem) {
                showIndex(parseInt(ownViewerItem.getAttribute('data-gallery-item'), 10));
            } else if (!isVisible(this.activeIndex)) {
                const visible = visibleIndexes();
                if (visible.length > 0) showIndex(visible[0]);
            }

            const ownMobileItem = this.querySelector('[data-mobile-gallery-item][data-variant-media~="' + idStr + '"]');
            if (this.mobileScroll && ownMobileItem) {
                this.mobileScroll.scrollTo({ left: ownMobileItem.offsetLeft - this.mobileScroll.offsetLeft, behavior: 'smooth' });
                if (this.setActiveMobileDot) {
                    this.setActiveMobileDot(parseInt(ownMobileItem.getAttribute('data-mobile-gallery-item'), 10));
                }
            } else if (this.mobileScroll && this.setActiveMobileDot && this.isMobileItemVisible) {
                const activeDot = Array.from(this.mobileDots || []).find((dot) => dot.classList.contains('is-active'));
                const activeIndex = activeDot ? parseInt(activeDot.getAttribute('data-mobile-gallery-dot'), 10) : -1;
                const activeItem = activeIndex !== -1 ? this.mobileItems[activeIndex] : null;
                if (!this.isMobileItemVisible(activeItem)) {
                    const firstVisibleIndex = Array.from(this.mobileItems).findIndex((item) => this.isMobileItemVisible(item));
                    const firstVisibleItem = firstVisibleIndex !== -1 ? this.mobileItems[firstVisibleIndex] : null;
                    if (firstVisibleItem) {
                        this.mobileScroll.scrollTo({ left: firstVisibleItem.offsetLeft - this.mobileScroll.offsetLeft, behavior: 'smooth' });
                        this.setActiveMobileDot(firstVisibleIndex);
                    }
                }
            }
        };
    }

    initMobileGallery() {
        this.mobileScroll = this.querySelector('[data-mobile-gallery-scroll]');
        this.mobileItems = this.querySelectorAll('[data-mobile-gallery-item]');
        this.mobileDots = this.querySelectorAll('[data-mobile-gallery-dot]');
        if (!this.mobileScroll || !this.mobileItems.length) return;

        this.isMobileItemVisible = (item) => !!item && !item.classList.contains('is-variant-hidden');

        this.setActiveMobileDot = (index) => {
            this.mobileDots.forEach((dot) => {
                dot.classList.toggle('is-active', dot.getAttribute('data-mobile-gallery-dot') === String(index));
            });
        };

        const activeDot = Array.from(this.mobileDots).find((dot) => dot.classList.contains('is-active'));
        if (activeDot) {
            const activeIndex = parseInt(activeDot.getAttribute('data-mobile-gallery-dot'), 10);
            const activeItem = this.mobileItems[activeIndex];
            if (activeItem) {
                this.mobileScroll.scrollLeft = activeItem.offsetLeft - this.mobileScroll.offsetLeft;
            }
        }

        this.mobileDots.forEach((dot) => {
            dot.addEventListener('click', () => {
                const index = parseInt(dot.getAttribute('data-mobile-gallery-dot'), 10);
                const target = this.mobileItems[index];
                if (target) {
                    this.mobileScroll.scrollTo({ left: target.offsetLeft - this.mobileScroll.offsetLeft, behavior: 'smooth' });
                }
            });
        });

        let scrollTimeout;
        this.mobileScroll.addEventListener('scroll', () => {
            clearTimeout(scrollTimeout);
            scrollTimeout = setTimeout(() => {
                const scrollLeft = this.mobileScroll.scrollLeft;
                let closestIndex = -1;
                let closestDistance = Infinity;
                this.mobileItems.forEach((item, index) => {
                    if (!this.isMobileItemVisible(item)) return;
                    const distance = Math.abs(item.offsetLeft - this.mobileScroll.offsetLeft - scrollLeft);
                    if (distance < closestDistance) {
                        closestDistance = distance;
                        closestIndex = index;
                    }
                });
                if (closestIndex !== -1) this.setActiveMobileDot(closestIndex);
            }, 100);
        });
    }

    initReviews() {
        this.reviewPanels = this.querySelectorAll('[data-review-panel]');
        this.reviewDots = this.querySelectorAll('[data-review-dot]');
        if (!this.reviewPanels.length) return;

        this.reviewInitialsEl = this.querySelector('[data-review-initials]');
        this.reviewNameEl = this.querySelector('[data-review-name]');
        this.reviewLocationEl = this.querySelector('[data-review-location]');
        this.reviewTimeEl = this.querySelector('[data-review-time]');
        this.reviewNavPrev = this.querySelector('[data-review-nav-prev]');
        this.reviewNavNext = this.querySelector('[data-review-nav-next]');
        this.reviewCardLinkEl = this.querySelector('[data-review-card-link]');

        const total = this.reviewPanels.length;
        const REVIEW_AUTOPLAY_MS = 5000;
        let currentIndex = 0;
        let autoplayTimer = null;

        const showReview = (index) => {
            currentIndex = ((index % total) + total) % total;

            this.reviewPanels.forEach((panel) => {
                const isMatch = panel.getAttribute('data-review-panel') === String(currentIndex);
                panel.classList.toggle('is-active', isMatch);
                if (isMatch) {
                    if (this.reviewInitialsEl) this.reviewInitialsEl.textContent = panel.getAttribute('data-initials') || '';
                    if (this.reviewNameEl) this.reviewNameEl.textContent = panel.getAttribute('data-name') || '';
                    if (this.reviewLocationEl) this.reviewLocationEl.textContent = panel.getAttribute('data-location') || '';
                    if (this.reviewTimeEl) this.reviewTimeEl.textContent = panel.getAttribute('data-time') || '';
                    if (this.reviewCardLinkEl && this.reviewCardLinkEl.tagName === 'A') {
                        const link = panel.getAttribute('data-link');
                        if (link) {
                            this.reviewCardLinkEl.setAttribute('href', link);
                        } else {
                            this.reviewCardLinkEl.removeAttribute('href');
                        }
                    }
                }
            });
            this.reviewDots.forEach((dot) => {
                dot.classList.toggle('is-active', dot.getAttribute('data-review-dot') === String(currentIndex));
            });
        };

        const stopAutoplay = () => {
            if (autoplayTimer) {
                clearInterval(autoplayTimer);
                autoplayTimer = null;
            }
        };

        const startAutoplay = () => {
            stopAutoplay();
            if (total <= 1) return;
            autoplayTimer = setInterval(() => {
                showReview(currentIndex + 1);
            }, REVIEW_AUTOPLAY_MS);
        };

        const goToReview = (index) => {
            showReview(index);
            startAutoplay();
        };

        this.reviewDots.forEach((dot) => {
            dot.addEventListener('click', (event) => {
                event.preventDefault();
                event.stopPropagation();
                goToReview(parseInt(dot.getAttribute('data-review-dot'), 10));
            });
        });

        if (this.reviewNavPrev) {
            this.reviewNavPrev.addEventListener('click', (event) => {
                event.preventDefault();
                event.stopPropagation();
                goToReview(currentIndex - 1);
            });
        }

        if (this.reviewNavNext) {
            this.reviewNavNext.addEventListener('click', (event) => {
                event.preventDefault();
                event.stopPropagation();
                goToReview(currentIndex + 1);
            });
        }

        showReview(0);
        startAutoplay();
    }

    initBiomarkerModals() {
        this.querySelectorAll('[data-modal-trigger]').forEach((trigger) => {
            trigger.addEventListener('click', (event) => {
                event.stopPropagation();
                const modalId = trigger.getAttribute('data-modal-target');
                const modal = this.querySelector('#' + modalId);
                if (modal) modal.hidden = false;
            });
        });

        this.querySelectorAll('[data-biomarker-modal-close]').forEach((closeEl) => {
            closeEl.addEventListener('click', () => {
                const modal = closeEl.closest('[data-biomarker-modal]');
                if (modal) modal.hidden = true;
            });
        });

        document.addEventListener('keydown', (event) => {
            if (event.key !== 'Escape') return;
            this.querySelectorAll('[data-biomarker-modal]:not([hidden])').forEach((modal) => {
                modal.hidden = true;
            });
        });
    }

    initVariantPicker() {
        const picker = this.querySelector('[data-variant-picker]');
        const variantsScript = this.querySelector('[data-product-variants]');
        if (!picker || !variantsScript) return;

        let variants;
        try {
            variants = JSON.parse(variantsScript.textContent);
        } catch (e) {
            return;
        }

        const idInput = this.querySelector('[data-variant-id-input]');
        const priceEl = this.querySelector('[data-variant-price]');
        const addBtn = this.querySelector('[data-add-to-cart-btn]');
        const addBtnLabel = this.querySelector('[data-add-to-cart-label-text]');

        this.selectedOptions = {};
        picker.querySelectorAll('[data-option-position]').forEach((wrap) => {
            const position = wrap.getAttribute('data-option-position');
            const activeBtn = wrap.querySelector('.homediq-product-main__variant-btn.is-active');
            if (activeBtn) {
                this.selectedOptions[position] = activeBtn.getAttribute('data-variant-value');
            }
        });

        const findMatchingVariant = () => {
            return variants.find((variant) => {
                return Object.keys(this.selectedOptions).every((position) => {
                    const key = 'option' + position;
                    return variant[key] === this.selectedOptions[position];
                });
            });
        };

        const applyVariant = (variant) => {
            if (!variant) return;

            if (idInput) idInput.value = variant.id;

            if (priceEl && variant.price_formatted) {
                priceEl.textContent = variant.price_formatted;
            }

            if (addBtn) {
                addBtn.disabled = !variant.available;
                if (addBtnLabel) {
                    addBtnLabel.textContent = variant.available
                        ? addBtn.getAttribute('data-add-to-cart-label')
                        : addBtn.getAttribute('data-sold-out-label');
                }
            }

            if (this.updateVariantMedia) this.updateVariantMedia(variant.id);
        };

        picker.querySelectorAll('[data-variant-value]').forEach((btn) => {
            btn.addEventListener('click', () => {
                const position = btn.getAttribute('data-variant-option-position');
                const value = btn.getAttribute('data-variant-value');

                this.selectedOptions[position] = value;

                picker
                    .querySelector('[data-option-position="' + position + '"]')
                    .querySelectorAll('[data-variant-value]')
                    .forEach((sibling) => {
                        const isMatch = sibling === btn;
                        sibling.classList.toggle('is-active', isMatch);
                        sibling.setAttribute('aria-pressed', isMatch ? 'true' : 'false');
                    });

                const matchedVariant = findMatchingVariant();
                applyVariant(matchedVariant);
            });
        });
    }

    initCollectionMethod() {
        const picker = this.querySelector('[data-collection-picker]');
        if (!picker) return;

        const options = picker.querySelectorAll('[data-collection-option]');
        const addBtn = this.querySelector('[data-add-to-cart-btn]');
        const addBtnProxy = this.querySelector('[data-add-to-cart-proxy]');
        const variantIdInput = this.querySelector('[data-variant-id-input]');
        const quantitySelector = this.querySelector('.homediq-product-main__quantity input[type="number"]');

        const patientForm = this.querySelector('[data-patient-form]');
        const patientForToggle = this.querySelector('[data-patient-for-toggle]');
        const patientNameFields = this.querySelector('[data-patient-name-fields]');
        const patientFirstNameInput = this.querySelector('[data-patient-first-name]');
        const patientLastNameInput = this.querySelector('[data-patient-last-name]');
        const patientDobInput = this.querySelector('[data-patient-dob]');
        const patientGenderInput = this.querySelector('[data-patient-gender]');

        this.selectedCollectionOption = 'self';
        this.selectedAddonVariantId = null;
        this.selectedPatientFor = 'self';

        const selectOption = (target) => {
            options.forEach((opt) => {
                const isMatch = opt === target;
                opt.classList.toggle('is-active', isMatch);
                opt.setAttribute('aria-checked', isMatch ? 'true' : 'false');
                opt.setAttribute('tabindex', isMatch ? '0' : '-1');
            });

            this.selectedCollectionOption = target.getAttribute('data-collection-option');
            this.selectedAddonVariantId = target.getAttribute('data-addon-variant-id') || null;

            if (patientForm) {
                const showPatientForm = this.selectedCollectionOption === 'blood_draw';
                patientForm.hidden = !showPatientForm;
                if (patientDobInput) patientDobInput.required = showPatientForm;
                if (patientGenderInput) patientGenderInput.required = showPatientForm;
            }
        };

        options.forEach((opt) => {
            opt.addEventListener('click', () => selectOption(opt));
            opt.addEventListener('keydown', (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    selectOption(opt);
                }
            });
        });

        if (patientForToggle) {
            const forOptions = patientForToggle.querySelectorAll('[data-patient-for-option]');

            const selectPatientFor = (target) => {
                forOptions.forEach((opt) => {
                    const isMatch = opt === target;
                    opt.classList.toggle('is-active', isMatch);
                    opt.setAttribute('aria-checked', isMatch ? 'true' : 'false');
                });

                this.selectedPatientFor = target.getAttribute('data-patient-for-option');
                const isOther = this.selectedPatientFor === 'other';

                if (patientNameFields) patientNameFields.hidden = !isOther;
                if (patientFirstNameInput) patientFirstNameInput.required = isOther;
                if (patientLastNameInput) patientLastNameInput.required = isOther;
            };

            forOptions.forEach((opt) => {
                opt.addEventListener('click', () => selectPatientFor(opt));
            });
        }

        if (!addBtn) return;

        addBtn.addEventListener('click', (event) => {
            if (!this.selectedAddonVariantId) return;
            if (!variantIdInput) return;
            if (addBtn.disabled) return;

            event.preventDefault();

            const mainVariantId = variantIdInput.value;
            const mainQuantity = quantitySelector ? Number(quantitySelector.value) || 1 : 1;
            const cartAddUrl = (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || '/cart/add.js';
            let items;

            if (this.selectedCollectionOption === 'blood_draw') {
                const isOther = this.selectedPatientFor === 'other';
                const fieldsToValidate = [patientDobInput, patientGenderInput];
                if (isOther) fieldsToValidate.push(patientFirstNameInput, patientLastNameInput);

                const isValid = fieldsToValidate.every((field) => !field || field.reportValidity());
                if (!isValid) return;

                const productForm = this.querySelector('product-form-component');
                const productId = (productForm && productForm.getAttribute('data-product-id')) || mainVariantId;
                const timestamp = Date.now();
                const birthDate = patientDobInput ? patientDobInput.value : '';
                const sex = patientGenderInput ? patientGenderInput.value.toLowerCase() : '';

                const mainItems = [];
                const serviceItems = [];

                for (let index = 0; index < mainQuantity; index++) {
                    const linkId = '_prikpost_link_' + productId + '_' + timestamp + '_' + index;

                    const serviceProperties = {
                        '_prikpost_link': linkId,
                        '_prikpost_service': 'true',
                        '_for_other': isOther ? 'true' : 'false',
                        'birth_date': birthDate,
                        'sex': sex,
                    };

                    if (isOther) {
                        serviceProperties['first_name'] = patientFirstNameInput ? patientFirstNameInput.value.trim() : '';
                        serviceProperties['last_name'] = patientLastNameInput ? patientLastNameInput.value.trim() : '';
                    }

                    mainItems.push({
                        id: mainVariantId,
                        quantity: 1,
                        properties: {
                            '_prikpost': 'true',
                            '_prikpost_variant_id': String(this.selectedAddonVariantId),
                            '_prikpost_link': linkId,
                        },
                    });
                    serviceItems.push({ id: this.selectedAddonVariantId, quantity: 1, properties: serviceProperties });
                }

                items = mainItems.concat(serviceItems);
            } else {
                items = [
                    { id: mainVariantId, quantity: mainQuantity },
                    { id: this.selectedAddonVariantId, quantity: 1, properties: { _blood_draw_for: String(mainVariantId) } },
                ];
            }

            addBtn.disabled = true;
            if (addBtnProxy) addBtnProxy.disabled = true;

            fetch(cartAddUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ items: items }),
            })
                .then((response) => response.json())
                .then((data) => {
                    if (data.status) {
                        console.error('Failed to add product with addon to cart:', data.description || data.message);
                        addBtn.disabled = false;
                        if (addBtnProxy) addBtnProxy.disabled = false;
                        return;
                    }
                    sessionStorage.removeItem('cart-count');

                    const reenable = () => {
                        addBtn.disabled = false;
                        if (addBtnProxy) addBtnProxy.disabled = false;
                    };

                    if (window.homediqRefreshCartDrawer) {
                        window.homediqRefreshCartDrawer({ action: 'add' }).then(reenable).catch(reenable);
                    } else {
                        reenable();
                    }
                })
                .catch((error) => {
                    console.error('Failed to add product with addon to cart:', error);
                    addBtn.disabled = false;
                    if (addBtnProxy) addBtnProxy.disabled = false;
                });
        });
    }

    initAddToCartProxy() {
        const proxyBtn = this.querySelector('[data-add-to-cart-proxy]');
        const realBtn = this.querySelector('[data-add-to-cart-btn]');
        if (!proxyBtn || !realBtn) return;

        proxyBtn.addEventListener('click', () => {
            if (realBtn.disabled) return;
            realBtn.click();
        });
    }

    initSubscriptionPicker() {
        const picker = this.querySelector('[data-subscription-picker]');
        if (!picker) return;

        const observeTarget = this.querySelector('product-form-component') || this;

        const trySetup = () => {
            const sealPlaceholder = this.querySelector('.sealsubs-target-element');
            if (!sealPlaceholder) return false;

            const sealOneTimeInput = sealPlaceholder.querySelector('input[data-type="one_time"]');
            const sealSubscribeInput = sealPlaceholder.querySelector('input[data-type="subscription"]');
            if (!sealOneTimeInput || !sealSubscribeInput) return false;

            this.setupSealSubscriptionSync(picker, sealPlaceholder, sealOneTimeInput, sealSubscribeInput);
            return true;
        };

        if (trySetup()) return;

        if (window.MutationObserver) {
            const observer = new MutationObserver(() => {
                if (trySetup()) observer.disconnect();
            });
            observer.observe(observeTarget, { childList: true, subtree: true });

            setTimeout(() => observer.disconnect(), 10000);
        }
    }

    setupSealSubscriptionSync(picker, sealRoot, sealOneTimeInput, sealSubscribeInput) {
        try {
            const sealIntervalSelect = sealRoot.querySelector('select[name="subs_interval"]');
            const sealOneTimeWrapper = sealOneTimeInput.closest('[role="radio"]') || sealOneTimeInput;
            const sealSubscribeWrapper = sealSubscribeInput.closest('[role="radio"]') || sealSubscribeInput;

            picker.hidden = false;

            const oneTimeOption = picker.querySelector('[data-subscription-option="one_time"]');
            const subscribeOption = picker.querySelector('[data-subscription-option="subscribe"]');
            const saveBadge = picker.querySelector('[data-subscription-save-badge]');
            const priceOneTimeEl = picker.querySelector('[data-subscription-price-onetime]');
            const priceSubscribeEl = picker.querySelector('[data-subscription-price-subscribe]');
            const intervalsWrap = picker.querySelector('[data-subscription-intervals]');
            const intervalPillsWrap = picker.querySelector('[data-subscription-interval-pills]');

            const addBtn = this.querySelector('[data-add-to-cart-btn]');
            const addBtnLabel = this.querySelector('[data-add-to-cart-label-text]');
            const cartIcon = this.querySelector('[data-cart-icon]');
            const subscribeIcon = this.querySelector('[data-subscribe-icon]');

            const syncPrices = () => {
                const oneTimeMoneyEl = sealRoot.querySelector('.sls-one-time-price .money');
                const subscribeMoneyEl = sealRoot.querySelector('.sls-selling-plan-group-price .money');
                const savingsBadgeEl = sealRoot.querySelector('.sls-savings-badge');

                if (priceOneTimeEl && oneTimeMoneyEl) {
                    priceOneTimeEl.textContent = oneTimeMoneyEl.textContent.trim();
                }

                if (priceSubscribeEl && subscribeMoneyEl) {
                    let text = subscribeMoneyEl.textContent.trim();
                    const select = sealRoot.querySelector('select[name="subs_interval"]');
                    const selectedOption = select ? select.options[select.selectedIndex] : null;
                    if (selectedOption) {
                        text += ' every ' + selectedOption.textContent.trim();
                    }
                    priceSubscribeEl.textContent = text;
                }

                if (saveBadge) {
                    const badgeText = savingsBadgeEl ? savingsBadgeEl.textContent.trim() : '';
                    if (badgeText) {
                        saveBadge.textContent = badgeText;
                        saveBadge.hidden = false;
                    } else {
                        saveBadge.hidden = true;
                    }
                }
            };

            const buildIntervalPills = () => {
                if (!sealIntervalSelect || !intervalPillsWrap) return;

                intervalPillsWrap.innerHTML = '';

                Array.from(sealIntervalSelect.options).forEach((option) => {
                    const pill = document.createElement('button');
                    pill.type = 'button';
                    pill.className = 'homediq-product-main__subscription-interval-pill';
                    pill.textContent = option.textContent.trim();
                    if (option.selected) pill.classList.add('is-active');

                    pill.addEventListener('click', () => {
                        sealIntervalSelect.value = option.value;
                        sealIntervalSelect.dispatchEvent(new Event('input', { bubbles: true }));
                        sealIntervalSelect.dispatchEvent(new Event('change', { bubbles: true }));

                        intervalPillsWrap.querySelectorAll('.homediq-product-main__subscription-interval-pill').forEach((p) => {
                            p.classList.toggle('is-active', p === pill);
                        });

                        syncPrices();
                    });

                    intervalPillsWrap.appendChild(pill);
                });
            };

            const selectOneTime = () => {
                oneTimeOption.classList.add('is-active');
                oneTimeOption.setAttribute('aria-pressed', 'true');
                subscribeOption.classList.remove('is-active');
                subscribeOption.setAttribute('aria-pressed', 'false');
                if (intervalsWrap) intervalsWrap.hidden = true;
                if (!sealOneTimeInput.checked) sealOneTimeWrapper.click();

                if (cartIcon) cartIcon.removeAttribute('hidden');
                if (subscribeIcon) subscribeIcon.setAttribute('hidden', '');
                if (addBtn && addBtnLabel && !addBtn.disabled) {
                    addBtnLabel.textContent = addBtn.getAttribute('data-add-to-cart-label');
                }
            };

            const selectSubscribe = () => {
                subscribeOption.classList.add('is-active');
                subscribeOption.setAttribute('aria-pressed', 'true');
                oneTimeOption.classList.remove('is-active');
                oneTimeOption.setAttribute('aria-pressed', 'false');
                if (intervalsWrap) intervalsWrap.hidden = false;
                if (!sealSubscribeInput.checked) sealSubscribeWrapper.click();

                if (cartIcon) cartIcon.setAttribute('hidden', '');
                if (subscribeIcon) subscribeIcon.removeAttribute('hidden');
                if (addBtn && addBtnLabel && !addBtn.disabled) {
                    addBtnLabel.textContent = addBtn.getAttribute('data-subscribe-label');
                }
            };

            oneTimeOption.addEventListener('click', selectOneTime);
            subscribeOption.addEventListener('click', selectSubscribe);

            if (window.MutationObserver) {
                const priceObserver = new MutationObserver(syncPrices);
                priceObserver.observe(sealRoot, { childList: true, subtree: true, characterData: true });
            }

            buildIntervalPills();
            syncPrices();

            if (sealSubscribeInput.checked) {
                selectSubscribe();
            }
        } catch (error) {
            console.error('Failed to initialize subscription picker:', error);
        }
    }
}

customElements.define('homediq-product-main', HomediqProductMain);

/* Article progress bar */
(function () {
    if (!document.body.classList.contains('template-article')) return;

    const bar = document.createElement('div');
    bar.className = 'homediq-reading-progress';
    bar.innerHTML = '<div class="homediq-reading-progress__fill"></div>';
    document.body.appendChild(bar);

    const fill = bar.querySelector('.homediq-reading-progress__fill');
    const target = document.querySelector('.homediq-blog-article__prose') || document.documentElement;
    const pageWrapper = document.querySelector('.page-wrapper');

    function getScrollContainer() {
        if (pageWrapper && pageWrapper.scrollHeight > pageWrapper.clientHeight) {
            return pageWrapper;
        }
        return null;
    }

    let ticking = false;

    function updateProgress() {
        const container = getScrollContainer();
        const targetRect = target.getBoundingClientRect();

        let scrollTop;
        let viewportHeight;
        let targetTop;

        if (container) {
            const containerRect = container.getBoundingClientRect();
            scrollTop = container.scrollTop;
            viewportHeight = container.clientHeight;
            targetTop = (targetRect.top - containerRect.top) + container.scrollTop;
        } else {
            scrollTop = window.scrollY;
            viewportHeight = window.innerHeight;
            targetTop = targetRect.top + window.scrollY;
        }

        const targetHeight = target.offsetHeight;
        const scrollableDistance = targetHeight - viewportHeight;

        let percent = scrollableDistance > 0
            ? ((scrollTop - targetTop) / scrollableDistance) * 100
            : 100;

        percent = Math.max(0, Math.min(100, percent));

        fill.style.width = percent + '%';
        ticking = false;
    }

    function onScroll() {
        if (!ticking) {
            window.requestAnimationFrame(updateProgress);
            ticking = true;
        }
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    if (pageWrapper) {
        pageWrapper.addEventListener('scroll', onScroll, { passive: true });
    }

    updateProgress();
})();

/* Homediq topbar - mobile swiper (749px and below) */
function initHomediqTopbarSwiper() {
    var el = document.querySelector('[data-topbar-swiper]');
    if (!el) return;

    var mediaQuery = window.matchMedia('(max-width: 749px)');
    var swiperInstance = null;

    function createSwiper() {
        if (swiperInstance) return;
        var slideCount = el.querySelectorAll('.swiper-slide').length;
        if (slideCount === 0) return;

        swiperInstance = new Swiper(el, {
            slidesPerView: 1,
            loop: slideCount > 1,
            autoplay: slideCount > 1 ? { delay: 3000, disableOnInteraction: false } : false
        });
    }

    function destroySwiper() {
        if (!swiperInstance) return;
        swiperInstance.destroy(true, true);
        swiperInstance = null;
    }

    if (mediaQuery.matches) createSwiper();

    mediaQuery.addEventListener('change', function (event) {
        if (event.matches) {
            createSwiper();
        } else {
            destroySwiper();
        }
    });
}

if (typeof Swiper !== 'undefined') {
    initHomediqTopbarSwiper();
} else {
    window.addEventListener('load', initHomediqTopbarSwiper);
}

/* Homediq B2B platform */
class HomediqB2BPlatformTabs extends HTMLElement {
    connectedCallback() {
        this.triggers = this.querySelectorAll('[data-tab-trigger]');
        this.panels = this.querySelectorAll('[data-tab-panel]');

        this.triggers.forEach((trigger) => {
            trigger.addEventListener('click', () => {
                const target = trigger.getAttribute('data-tab-trigger');

                this.triggers.forEach((t) => {
                    const isMatch = t === trigger;
                    t.classList.toggle('is-active', isMatch);
                    t.setAttribute('aria-selected', isMatch ? 'true' : 'false');
                });

                this.panels.forEach((panel) => {
                    const isMatch = panel.getAttribute('data-tab-panel') === target;
                    panel.classList.toggle('is-active', isMatch);
                    panel.hidden = !isMatch;
                });
            });
        });
    }
}

customElements.define('homediq-b2b-platform-tabs', HomediqB2BPlatformTabs);

/* Biomarker tab open */
document.querySelectorAll('[data-open-tab]').forEach(function (link) {
    link.addEventListener('click', function (event) {
        var targetTab = link.getAttribute('data-open-tab');
        var tabsRoot = document.querySelector('homediq-product-tabs');
        var trigger = tabsRoot ? tabsRoot.querySelector('[data-tab-trigger="' + targetTab + '"]') : null;

        if (trigger) {
            event.preventDefault();
            trigger.click();
            tabsRoot.closest('.homediq-product-tabs-wrap').scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    });
});

/* Homediq B2B header - keep --header-group-height in sync, since Horizon's own script in theme.liquid only looks for <header-component> */
(function () {
    var header = document.querySelector('.homediq-b2b-header-wrap');
    var headerGroup = document.querySelector('#header-group');
    if (!header || !headerGroup) return;

    function updateHeaderVars() {
        var headerGroupHeight = 0;
        Array.prototype.forEach.call(headerGroup.children, function (el) {
            if (el instanceof HTMLElement) headerGroupHeight += el.offsetHeight;
        });

        document.body.style.setProperty('--header-height', header.offsetHeight + 'px');
        document.body.style.setProperty('--header-group-height', headerGroupHeight + 'px');
    }

    updateHeaderVars();
    window.addEventListener('resize', updateHeaderVars);
    window.addEventListener('load', updateHeaderVars);
})();

/* B2B split functionality */
if (!window.__homediqB2bModeBound) {
    window.__homediqB2bModeBound = true;

    var HOMEDIQ_B2B_COOKIE = 'homediq_b2b_mode';
    var HOMEDIQ_B2B_COOKIE_MAX_AGE = 60 * 60 * 24 * 30;

    function homediqSetB2bCookie() {
        document.cookie = HOMEDIQ_B2B_COOKIE + '=1; path=/; max-age=' + HOMEDIQ_B2B_COOKIE_MAX_AGE + '; SameSite=Lax';
    }

    function homediqClearB2bCookie() {
        document.cookie = HOMEDIQ_B2B_COOKIE + '=; path=/; max-age=0; SameSite=Lax';
    }

    if (document.body.classList.contains('b2b')) {
        homediqSetB2bCookie();
    }

    document.addEventListener('click', function (event) {
        var consumersLink = event.target.closest('[data-homediq-consumers-link]');
        if (!consumersLink) return;

        homediqClearB2bCookie();
    });
}

/* Homediq available at - swiper */
function initHomediqAvailableSwiper() {
    document.querySelectorAll('[data-homediq-available-swiper]').forEach(function (el) {
        if (el.dataset.swiperInitialized) return;
        el.dataset.swiperInitialized = 'true';

        var slideCount = el.querySelectorAll('.swiper-slide').length;

        new Swiper(el, {
            slidesPerView: 2,
            spaceBetween: 32,
            loop: slideCount > 1,
            autoplay: slideCount > 1 ? { delay: 3000, disableOnInteraction: false } : false,
            breakpoints: {
                750: { slidesPerView: 5, spaceBetween: 56 }
            }
        });
    });
}

if (typeof Swiper !== 'undefined') {
    initHomediqAvailableSwiper();
} else {
    window.addEventListener('load', initHomediqAvailableSwiper);
}

/* Homediq retail partners - swiper */
function initHomediqRetailPartnersSwiper() {
    document.querySelectorAll('[data-homediq-retail-partners-swiper]').forEach(function (el) {
        if (el.dataset.swiperInitialized) return;
        el.dataset.swiperInitialized = 'true';

        var slideCount = el.querySelectorAll('.swiper-slide').length;

        new Swiper(el, {
            slidesPerView: 2,
            spaceBetween: 32,
            loop: slideCount > 1,
            autoplay: slideCount > 1 ? { delay: 3000, disableOnInteraction: false } : false,
            breakpoints: {
                750: { slidesPerView: 5, spaceBetween: 56 }
            }
        });
    });
}

if (typeof Swiper !== 'undefined') {
    initHomediqRetailPartnersSwiper();
} else {
    window.addEventListener('load', initHomediqRetailPartnersSwiper);
}

/* Close cart drawer when clicked outside */
document.addEventListener('click', function (event) {
    var drawer = document.getElementById('cart-drawer');
    if (!drawer || !drawer.hasAttribute('open')) return;

    if (event.target === document.body) {
        drawer.close();
        return;
    }

    var dialog = drawer.querySelector('.theme-drawer__dialog');
    if (dialog && dialog.open && event.target === dialog) {
        drawer.close();
    }
});

/* Last menu item target blank + localized href (header + footer) */
function addTargetBlankToLastMenuItem() {
  const lang = document.documentElement.lang.slice(0, 2).toLowerCase();
  const localizedHrefs = {
    nl: 'https://hmq.codes/hc-contact-nl',
    de: 'https://hmq.codes/hc-contact-de'
  };
  const newHref = localizedHrefs[lang];

  const localizedAccountHrefs = {
    nl: 'https://hmq.codes/account-nl/',
    en: 'https://hmq.codes/account-en/',
    de: 'https://hmq.codes/account-de/'
  };
  const newAccountHref = localizedAccountHrefs[lang];

  const desktopLink = document.querySelector(
    'nav.homediq-header__nav > a.homediq-header__nav-link:last-of-type'
  );
  if (desktopLink) {
    desktopLink.target = '_blank';
    desktopLink.rel = 'noopener noreferrer';
    if (newHref) desktopLink.href = newHref;
  }

  const mobileLinkGroups = document.querySelectorAll('.homediq-header__mobile-links');
  if (mobileLinkGroups.length >= 2) {
    const mainMenuGroup = mobileLinkGroups[mobileLinkGroups.length - 2];
    const mobileLinks = mainMenuGroup.querySelectorAll('a.homediq-header__mobile-link');
    const lastMobileLink = mobileLinks[mobileLinks.length - 1];
    if (lastMobileLink) {
      lastMobileLink.target = '_blank';
      lastMobileLink.rel = 'noopener noreferrer';
      if (newHref) lastMobileLink.href = newHref;
    }
  }

  const footerCol = document.querySelector('.homediq-footer__col:nth-child(4)');
  if (footerCol) {
    const footerLinks = footerCol.querySelectorAll('li a');
    const lastFooterLink = footerLinks[footerLinks.length - 1];
    if (lastFooterLink) {
      lastFooterLink.target = '_blank';
      lastFooterLink.rel = 'noopener noreferrer';
      if (newHref) lastFooterLink.href = newHref;
    }
  }

  const footerColThreeLink = document.querySelector('.homediq-footer__col:nth-child(3) li:last-child a');
  if (footerColThreeLink && newAccountHref) {
    footerColThreeLink.href = newAccountHref;
  }
}

document.addEventListener('DOMContentLoaded', addTargetBlankToLastMenuItem);
document.addEventListener('shopify:section:load', addTargetBlankToLastMenuItem);

/* Add Impressum link to "Rechtliches" footer column (Germany market only) */
function addImpressumLinkToFooter() {
  if (!document.body.classList.contains('market-germany')) return;

  document.querySelectorAll('.homediq-footer__col-heading').forEach((heading) => {
    if (!heading.textContent.includes('Rechtliches')) return;

    const list = heading.parentElement.querySelector('.homediq-footer__link-list');
    if (!list) return;
    if (list.querySelector('a[href="/pages/impressum"]')) return;

    const li = document.createElement('li');
    li.innerHTML = `
      <a href="/pages/impressum" class="homediq-footer__link">
        Impressum
      </a>
    `;

    const items = list.querySelectorAll('li');
    const seventhItem = items[6]; // insert Impressum as the 7th item
    if (seventhItem) {
      list.insertBefore(li, seventhItem);
    } else {
      list.appendChild(li);
    }
  });
}

document.addEventListener('DOMContentLoaded', addImpressumLinkToFooter);
document.addEventListener('shopify:section:load', addImpressumLinkToFooter);

/* Translate "General health" badge/tag/pill text by language */
function translateGeneralHealthLabel() {
  const lang = document.documentElement.lang.slice(0, 2).toLowerCase();

  const translations = {
    en: 'General health',
    de: 'Allgemeine Gesundheit',
    nl: 'Algemene gezondheid'
  };

  const newText = translations[lang];
  if (!newText) return;

  const selector = [
    '.homediq-blog__badge',
    '.homediq-blog-article__tag',
    '.homediq-blog-list__pill',
    '.homediq-product-blog__tag',
    '.homediq-blog-list__card-badge'
  ].join(', ');

  document.querySelectorAll(selector).forEach((el) => {
    el.childNodes.forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE && /general health/i.test(node.textContent)) {
        node.textContent = node.textContent.replace(/general health/i, newText);
      }
    });
  });
}

document.addEventListener('DOMContentLoaded', translateGeneralHealthLabel);
document.addEventListener('shopify:section:load', translateGeneralHealthLabel);